import { EnvironmentConfig, ConfigValidationError, ConfigValidationResult, Environment } from './types';

/**
 * Validates environment configuration
 */
export class ConfigValidator {
  private errors: ConfigValidationError[] = [];
  private warnings: ConfigValidationError[] = [];

  /**
   * Validates the complete environment configuration
   */
  validate(config: EnvironmentConfig): ConfigValidationResult {
    this.errors = [];
    this.warnings = [];

    this.validateEnvironment(config.environment);
    this.validateApiConfiguration(config);
    this.validateCognitoConfiguration(config);
    this.validateCdnConfiguration(config);
    this.validateFeatureFlags(config);
    this.validateSecurityConfiguration(config);
    this.validatePerformanceConfiguration(config);

    return {
      isValid: this.errors.length === 0,
      errors: this.errors,
      warnings: this.warnings,
    };
  }

  private validateEnvironment(environment: Environment): void {
    const validEnvironments: Environment[] = ['development', 'staging', 'production'];
    
    if (!validEnvironments.includes(environment)) {
      this.addError('environment', `Invalid environment: ${environment}. Must be one of: ${validEnvironments.join(', ')}`, environment);
    }
  }

  private validateApiConfiguration(config: EnvironmentConfig): void {
    // Validate API Base URL
    if (!config.apiBaseUrl) {
      this.addError('apiBaseUrl', 'API Base URL is required');
    } else if (!this.isValidUrl(config.apiBaseUrl)) {
      this.addError('apiBaseUrl', 'API Base URL must be a valid URL', config.apiBaseUrl);
    }

    // Validate API Timeout
    if (!config.apiTimeout || config.apiTimeout <= 0) {
      this.addError('apiTimeout', 'API timeout must be a positive number', config.apiTimeout);
    } else if (config.apiTimeout < 5000) {
      this.addWarning('apiTimeout', 'API timeout is less than 5 seconds, which may cause issues with slow connections', config.apiTimeout);
    } else if (config.apiTimeout > 30000) {
      this.addWarning('apiTimeout', 'API timeout is greater than 30 seconds, which may cause poor user experience', config.apiTimeout);
    }
  }

  private validateCognitoConfiguration(config: EnvironmentConfig): void {
    // Validate Cognito User Pool ID
    if (!config.cognitoUserPoolId) {
      this.addError('cognitoUserPoolId', 'Cognito User Pool ID is required');
    } else if (!this.isValidCognitoUserPoolId(config.cognitoUserPoolId)) {
      this.addError('cognitoUserPoolId', 'Cognito User Pool ID format is invalid', config.cognitoUserPoolId);
    }

    // Validate Cognito User Pool Client ID
    if (!config.cognitoUserPoolClientId) {
      this.addError('cognitoUserPoolClientId', 'Cognito User Pool Client ID is required');
    }

    // Validate Cognito Hosted UI URL
    if (!config.cognitoHostedUiUrl) {
      this.addError('cognitoHostedUiUrl', 'Cognito Hosted UI URL is required');
    } else if (!this.isValidUrl(config.cognitoHostedUiUrl)) {
      this.addError('cognitoHostedUiUrl', 'Cognito Hosted UI URL must be a valid URL', config.cognitoHostedUiUrl);
    } else if (!config.cognitoHostedUiUrl.includes('amazoncognito.com')) {
      this.addWarning('cognitoHostedUiUrl', 'Cognito Hosted UI URL should be an Amazon Cognito domain', config.cognitoHostedUiUrl);
    }

    // Validate Cognito Region
    if (!config.cognitoRegion) {
      this.addError('cognitoRegion', 'Cognito region is required');
    } else if (!this.isValidAwsRegion(config.cognitoRegion)) {
      this.addError('cognitoRegion', 'Invalid AWS region format', config.cognitoRegion);
    }
  }

  private validateCdnConfiguration(config: EnvironmentConfig): void {
    // Validate Images CDN URL
    if (!config.imagesCdnUrl) {
      this.addError('imagesCdnUrl', 'Images CDN URL is required');
    } else if (!this.isValidUrl(config.imagesCdnUrl)) {
      this.addError('imagesCdnUrl', 'Images CDN URL must be a valid URL', config.imagesCdnUrl);
    }

    // Validate Static Assets CDN URL (optional)
    if (config.staticAssetsCdnUrl && !this.isValidUrl(config.staticAssetsCdnUrl)) {
      this.addError('staticAssetsCdnUrl', 'Static Assets CDN URL must be a valid URL', config.staticAssetsCdnUrl);
    }
  }

  private validateFeatureFlags(config: EnvironmentConfig): void {
    // Validate boolean feature flags
    const booleanFlags = [
      'enableDebugMode',
      'enableAnalytics',
      'enablePerformanceMonitoring',
      'enableErrorReporting',
      'enableDevTools',
      'enableHotReload',
      'enableCSP',
      'enableServiceWorker',
      'enableLazyLoading'
    ] as const;

    booleanFlags.forEach(flag => {
      if (typeof config[flag] !== 'boolean') {
        this.addError(flag, `${flag} must be a boolean value`, config[flag]);
      }
    });

    // Environment-specific warnings
    if (config.environment === 'production') {
      if (config.enableDebugMode) {
        this.addWarning('enableDebugMode', 'Debug mode should be disabled in production');
      }
      if (config.enableDevTools) {
        this.addWarning('enableDevTools', 'Dev tools should be disabled in production');
      }
      if (config.enableHotReload) {
        this.addWarning('enableHotReload', 'Hot reload should be disabled in production');
      }
    }
  }

  private validateSecurityConfiguration(config: EnvironmentConfig): void {
    // Validate allowed origins
    if (!Array.isArray(config.allowedOrigins)) {
      this.addError('allowedOrigins', 'Allowed origins must be an array');
    } else {
      config.allowedOrigins.forEach((origin, index) => {
        if (!this.isValidUrl(origin)) {
          this.addError(`allowedOrigins[${index}]`, 'Each allowed origin must be a valid URL', origin);
        }
      });

      if (config.allowedOrigins.length === 0) {
        this.addWarning('allowedOrigins', 'No allowed origins specified, which may cause CORS issues');
      }
    }

    // Security recommendations
    if (config.environment === 'production' && !config.enableCSP) {
      this.addWarning('enableCSP', 'Content Security Policy should be enabled in production for better security');
    }
  }

  private validatePerformanceConfiguration(config: EnvironmentConfig): void {
    // Validate max retries
    if (!Number.isInteger(config.maxRetries) || config.maxRetries < 0) {
      this.addError('maxRetries', 'Max retries must be a non-negative integer', config.maxRetries);
    } else if (config.maxRetries > 5) {
      this.addWarning('maxRetries', 'Max retries greater than 5 may cause poor user experience', config.maxRetries);
    }

    // Validate retry delay
    if (!Number.isInteger(config.retryDelay) || config.retryDelay <= 0) {
      this.addError('retryDelay', 'Retry delay must be a positive integer', config.retryDelay);
    } else if (config.retryDelay < 500) {
      this.addWarning('retryDelay', 'Retry delay less than 500ms may overwhelm the server', config.retryDelay);
    } else if (config.retryDelay > 5000) {
      this.addWarning('retryDelay', 'Retry delay greater than 5 seconds may cause poor user experience', config.retryDelay);
    }
  }

  private addError(field: string, message: string, value?: any): void {
    this.errors.push({ field, message, value });
  }

  private addWarning(field: string, message: string, value?: any): void {
    this.warnings.push({ field, message, value });
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private isValidCognitoUserPoolId(userPoolId: string): boolean {
    // Cognito User Pool ID format: us-east-1_123456789
    const cognitoUserPoolIdRegex = /^[a-z0-9-]+_[a-zA-Z0-9]+$/;
    return cognitoUserPoolIdRegex.test(userPoolId);
  }

  private isValidAwsRegion(region: string): boolean {
    // AWS region format: us-east-1, eu-west-1, etc.
    const awsRegionRegex = /^[a-z]{2}-[a-z]+-\d+$/;
    return awsRegionRegex.test(region);
  }
}

/**
 * Validates environment configuration and throws error if invalid
 */
export function validateConfig(config: EnvironmentConfig): void {
  const validator = new ConfigValidator();
  const result = validator.validate(config);

  if (!result.isValid) {
    const errorMessages = result.errors.map(error => `${error.field}: ${error.message}`);
    throw new Error(`Configuration validation failed:\n${errorMessages.join('\n')}`);
  }

  // Log warnings in development
  if (result.warnings.length > 0 && config.enableDebugMode) {
    console.warn('Configuration warnings:');
    result.warnings.forEach(warning => {
      console.warn(`- ${warning.field}: ${warning.message}`);
    });
  }
}

/**
 * Validates required environment variables
 */
export function validateRequiredEnvVars(requiredVars: string[]): void {
  const missingVars = requiredVars.filter(varName => !import.meta.env[varName]);
  
  if (missingVars.length > 0) {
    throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
  }
}